/* eslint-disable react/require-default-props */
import { AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Flex, Text } from '@chakra-ui/core';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { IoCheckmarkSharp } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import colors from '../../styles/colors';

interface TodoAccordionItemProps<T extends { [key: string]: number }> {
  title: string;
  icon: React.ReactNode;
  totalCount: number;
  items?: T;
  status?: string;
  filterByStatus: string;
  setFilterByStatus: (status: string) => void;
  renderCustomBadge: (countNumber: number) => React.ReactNode;
}

export const TodoAccordionItem = React.memo(function TodoAccordionItem<T extends { [key: string]: number }>({
  title,
  icon,
  totalCount,
  items,
  status,
  filterByStatus,
  setFilterByStatus,
  renderCustomBadge,
}: TodoAccordionItemProps<T>) {
  const { t } = useTranslation('toDoList');

  if (status) {
    return (
      <Flex
        as="span"
        flex="1"
        textAlign="left"
        alignItems="center"
        background="#F7FAFC"
        p="12px"
        borderTop={`1px solid ${colors.neutral[200]}`}
        justifyContent="space-between"
        onClick={() => setFilterByStatus(status)}
        data-testid={`status-${status}`}
        cursor="pointer"
      >
        <Flex flex="1" textAlign="left" alignItems="center">
          {icon}
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize="14px" fontWeight={700} ml="8px">
              {t(title)}
            </Text>

            {filterByStatus === status && (
              <Box as="span" color="blue.500" fontSize="18px" ml={2}>
                <IoCheckmarkSharp />
              </Box>
            )}
          </Flex>
        </Flex>
        {renderCustomBadge(totalCount)}
      </Flex>
    );
  }

  return (
    <AccordionItem borderTop={`1px solid ${colors.neutral[200]}`}>
      <h2>
        <AccordionButton>
          <Flex as="span" flex="1" textAlign="left" alignItems="center">
            {icon}
            <Text fontSize="14px" fontWeight={700} ml="8px">
              {t(title)}
            </Text>
          </Flex>
          {renderCustomBadge(totalCount)}
          {items && !isEmpty(items) && <AccordionIcon />}
        </AccordionButton>
      </h2>
      {items && !isEmpty(items) && (
        <AccordionPanel p={0}>
          {Object.entries(items).map(
            ([key, value]) =>
              value !== 0 && (
                <Flex
                  key={key}
                  as="span"
                  flex="1"
                  textAlign="left"
                  alignItems="center"
                  background="#F7FAFC"
                  p="12px"
                  border="1px solid var(--Outline-Border-Color, #E5E7EB);"
                  justifyContent="space-between"
                  onClick={() => setFilterByStatus(key)}
                  data-testid={`status-${key}`}
                >
                  <Text d="inline-flex" alignItems="center" fontSize="14px" fontWeight={400}>
                    {t(`${key}`)}
                    {filterByStatus === key && (
                      <Box as="span" color="blue.500" fontSize="18px" ml={2}>
                        <IoCheckmarkSharp />
                      </Box>
                    )}
                  </Text>
                  {renderCustomBadge(value)}
                </Flex>
              ),
          )}
        </AccordionPanel>
      )}
    </AccordionItem>
  );
}) as <T extends { [key: string]: number }>(props: TodoAccordionItemProps<T>) => React.ReactElement;
